import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from './ui/button';
import { mockData } from '../data/mock';

const PromoModal = ({ isOpen, onClose, promoType, onAddToCart }) => {
  const [selectedPizzas, setSelectedPizzas] = useState({
    pizza1: { name: '', size: '' },
    pizza2: { name: '', size: '' },
    pizza3: { name: '', size: '' }
  });

  if (!isOpen) return null;

  // Récupérer toutes les pizzas disponibles
  const getAllPizzaNames = () => {
    const tomatoBase = mockData.menuCategories.find(cat => cat.name.includes('PIZZAS BASE SAUCE TOMATE'));
    const cremeBase = mockData.menuCategories.find(cat => cat.name.includes('PIZZAS BASE CRÈME'));
    return [
      ...(tomatoBase?.items || []).map(p => ({ name: p.name, basePrice: p.basePrice })),
      ...(cremeBase?.items || []).map(p => ({ name: p.name, basePrice: p.basePrice }))
    ];
  };

  const pizzaList = getAllPizzaNames();
  const isPromo1 = promoType === 'promo1_livraison';
  const isPromo2 = promoType === 'promo2_emporter';
  const isPromo3 = promoType === 'promo3_emporter_50';

  const handlePizzaSelect = (pizzaKey, field, value) => {
    setSelectedPizzas(prev => ({
      ...prev,
      [pizzaKey]: {
        ...prev[pizzaKey],
        [field]: value
      }
    }));
  };

  const isValidSelection = () => {
    if (isPromo1) {
      // Promo 1 : 3 pizzas nécessaires (Senior ou Mega, hors Classica)
      const valid = selectedPizzas.pizza1.name && selectedPizzas.pizza1.size &&
                    selectedPizzas.pizza2.name && selectedPizzas.pizza2.size &&
                    selectedPizzas.pizza3.name && selectedPizzas.pizza3.size &&
                    ['Senior', 'Mega'].includes(selectedPizzas.pizza1.size) &&
                    ['Senior', 'Mega'].includes(selectedPizzas.pizza2.size) &&
                    ['Senior', 'Mega'].includes(selectedPizzas.pizza3.size);
      
      if (valid) {
        return !selectedPizzas.pizza1.name.toLowerCase().includes('classica') &&
               !selectedPizzas.pizza2.name.toLowerCase().includes('classica') &&
               !selectedPizzas.pizza3.name.toLowerCase().includes('classica');
      }
      return false;
    } else if (isPromo2 || isPromo3) {
      // Promo 2 & 3 : 2 pizzas nécessaires (Senior ou Mega, hors Classica)
      const valid = selectedPizzas.pizza1.name && selectedPizzas.pizza1.size &&
                    selectedPizzas.pizza2.name && selectedPizzas.pizza2.size &&
                    ['Senior', 'Mega'].includes(selectedPizzas.pizza1.size) &&
                    ['Senior', 'Mega'].includes(selectedPizzas.pizza2.size);
      
      if (valid) {
        return !selectedPizzas.pizza1.name.toLowerCase().includes('classica') &&
               !selectedPizzas.pizza2.name.toLowerCase().includes('classica');
      }
      
      return false;
    }
    return false;
  };

  const getPizzaPrice = (pizzaName, size) => {
    const pizza = pizzaList.find(p => p.name === pizzaName);
    if (!pizza || !pizza.basePrice) return 0;
    const sizeKey = size.toLowerCase();
    return pizza.basePrice[sizeKey] || 0;
  };

  const handleAddToCart = () => {
    if (!isValidSelection()) {
      const errorMsg = isPromo1
        ? 'Veuillez sélectionner 3 pizzas Senior ou Mega (hors Classica)'
        : 'Veuillez sélectionner 2 pizzas Senior ou Mega (hors Classica)';
      alert(errorMsg);
      return;
    }

    const pizzasToAdd = isPromo1 
      ? [selectedPizzas.pizza1, selectedPizzas.pizza2, selectedPizzas.pizza3]
      : [selectedPizzas.pizza1, selectedPizzas.pizza2];

    pizzasToAdd.forEach((pizza, index) => {
      const price = getPizzaPrice(pizza.name, pizza.size);
      const cartItem = {
        id: `promo-${promoType}-${pizza.name}-${pizza.size}-${Date.now()}-${index}`,
        name: `Pizza – ${pizza.name} - Taille: ${pizza.size}`,
        price: `${price.toFixed(2)} €`,
        description: `Pizza ${pizza.size}`,
        category: '🍕 PIZZAS',
        quantity: 1,
        forcePromoType: promoType, // Marquer avec le type de promo choisi
        options: {
          pizzaSize: pizza.size,
          optionsText: `Taille: ${pizza.size}`
        }
      };
      onAddToCart(cartItem);
    });

    // Réinitialiser et fermer
    setSelectedPizzas({
      pizza1: { name: '', size: '' },
      pizza2: { name: '', size: '' },
      pizza3: { name: '', size: '' }
    });
    onClose();
  };

  const handleClose = () => {
    setSelectedPizzas({
      pizza1: { name: '', size: '' },
      pizza2: { name: '', size: '' },
      pizza3: { name: '', size: '' }
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-testid="promo-modal">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-[#D47A2A] text-white">
          <h2 className="text-xl font-bold" data-testid="promo-modal-title">
            {isPromo1 
              ? '🎉 Promo Livraison : 2 pizzas achetées = 3ème offerte'
              : isPromo2
                ? '🎉 Promo À Emporter : 2ème pizza réduite'
                : '🎉 Promo : 2ème pizza à -50%'}
          </h2>
          <button onClick={handleClose} className="text-white hover:text-gray-200" data-testid="promo-modal-close-btn">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          <div className="mb-6 p-4 bg-orange-50 border border-[#D47A2A] rounded-lg">
            <p className="text-sm text-gray-900 font-medium">
              {isPromo1 
                ? '📍 Sélectionnez 3 pizzas Senior ou Mega (hors Classica). La moins chère sera offerte. Commande en livraison uniquement.'
                : isPromo2
                  ? '📍 Sélectionnez 2 pizzas Senior ou Mega (hors Classica). La 2ème sera à 2€ (Senior) ou 4€ (Mega). À emporter uniquement.'
                  : '📍 Sélectionnez 2 pizzas Senior ou Mega (hors Classica). La moins chère sera à -50%. Valable en livraison ou à emporter.'}
            </p>
          </div>

          {/* Pizza Selection */}
          <div className="space-y-6">
            {[1, 2, ...(isPromo1 ? [3] : [])].map(num => (
              <div key={num} className="border border-gray-300 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3" data-testid={`promo-pizza-${num}-title`}>
                  Pizza {num} {num === 3 && isPromo1 ? '(Offerte - la moins chère)' : ''}
                  {num === 2 && isPromo2 ? '(Réduite à 2€ ou 4€)' : ''}
                  {num === 2 && isPromo3 ? '(-50% sur la moins chère)' : ''}
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Choix de la pizza */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Choisir la pizza *
                    </label>
                    <select
                      value={selectedPizzas[`pizza${num}`].name}
                      onChange={(e) => handlePizzaSelect(`pizza${num}`, 'name', e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-lg text-gray-900 focus:border-[#D47A2A] focus:ring-1 focus:ring-[#D47A2A]"
                      data-testid={`promo-pizza-${num}-select`}
                    >
                      <option value="">-- Sélectionner une pizza --</option>
                      {pizzaList.map(pizza => {
                        // Filtrer Classica pour toutes les promos (promo1, promo2, promo3)
                        if (pizza.name.toLowerCase().includes('classica')) {
                          return null;
                        }
                        return <option key={pizza.name} value={pizza.name}>{pizza.name}</option>;
                      })}
                    </select>
                  </div>

                  {/* Choix de la taille */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Taille * (Senior ou Mega uniquement)
                    </label>
                    <div className="space-y-2">
                      {['Senior', 'Mega'].map(size => {
                        const price = selectedPizzas[`pizza${num}`].name 
                          ? getPizzaPrice(selectedPizzas[`pizza${num}`].name, size)
                          : 0;
                        
                        return (
                          <button
                            key={size}
                            onClick={() => handlePizzaSelect(`pizza${num}`, 'size', size)}
                            disabled={!selectedPizzas[`pizza${num}`].name}
                            className={`w-full p-3 text-left border rounded-lg transition-colors ${
                              selectedPizzas[`pizza${num}`].size === size
                                ? 'border-[#D47A2A] bg-[#D47A2A] text-white'
                                : selectedPizzas[`pizza${num}`].name
                                  ? 'border-gray-300 hover:border-[#D47A2A] text-gray-900'
                                  : 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                            }`}
                            data-testid={`promo-pizza-${num}-size-${size.toLowerCase()}`}
                          >
                            <div className="flex justify-between items-center">
                              <span>{size}</span>
                              {selectedPizzas[`pizza${num}`].name && (
                                <span className="font-bold">{price.toFixed(2)}€</span>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t p-4 bg-gray-50">
          <div className="flex space-x-3">
            <Button
              variant="outline"
              onClick={handleClose}
              className="flex-1 border-gray-400 text-gray-900 bg-white hover:bg-gray-100"
              data-testid="promo-modal-cancel-btn"
            >
              Annuler
            </Button>
            <Button
              onClick={handleAddToCart}
              disabled={!isValidSelection()}
              className="flex-1 bg-[#D47A2A] hover:bg-[#B8661F] text-white disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="promo-modal-add-btn"
            >
              {isPromo1 
                ? 'Ajouter au panier (Livraison)' 
                : isPromo2 
                  ? 'Ajouter au panier (À emporter)'
                  : 'Ajouter au panier'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromoModal;
